import {
  ArrowRight,
  CheckCircle,
  Clock,
  Mail,
  RefreshCw,
  Shield,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

const VerifyAccount = () => {
  const { verifyAccount, verificationCode, loading } = useAffiliateUserStore();
  const navigate = useNavigate();

  // Form fields
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Field validation states
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    // Get email from localStorage if available
    const storedEmail = localStorage.getItem('affiliateEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Validation functions
  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const validateCode = (value) => {
    if (!value.trim()) return 'Verification code is required';
    if (value.trim().length !== 4)
      return 'Verification code must be 4 characters';
    if (!/^[A-Za-z0-9]+$/.test(value.trim()))
      return 'Verification code must contain only letters and numbers';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    if (touchedFields.email) newErrors.email = validateEmail(email);
    if (touchedFields.code) newErrors.code = validateCode(code);
    setErrors(newErrors);
  }, [email, code, touchedFields]);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleVerify = async () => {
    // Mark all fields as touched for validation
    const allTouched = { email: true, code: true };
    setTouchedFields(allTouched);

    // Validate all fields
    const emailError = validateEmail(email);
    const codeError = validateCode(code);

    if (emailError || codeError) {
      setErrors({
        email: emailError,
        code: codeError,
      });
      Error('Please fix the form errors before submitting');
      return;
    }

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        verificationCode: code.trim(),
      };

      const response = await verifyAccount(payload);

      // Check for success
      if (response?.success && (response?.user || response?.access_token)) {
        Success('Email verified successfully! Welcome to our partner program.');
        localStorage.removeItem('affiliateEmail');
        navigate('/dashboard');
      } else if (response?.error) {
        // Handle error returned from store
        Error(response.error);
      } else if (response?.success === false) {
        // Handle generic failure
        Error(
          response?.message || 'Invalid verification code. Please try again.'
        );
      } else {
        // Fallback error
        Error('Verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      // Only show error if it's not already handled
      Error(
        error?.response?.data?.message ||
          'Verification failed. Please try again.'
      );
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    const emailError = validateEmail(email);
    if (emailError) {
      setTouchedFields((prev) => ({ ...prev, email: true }));
      setErrors((prev) => ({ ...prev, email: emailError }));
      Error('Please enter a valid email address');
      return;
    }

    try {
      setIsResending(true);
      const payload = {
        email: email.trim().toLowerCase(),
      };

      await verificationCode(payload);
      Success('Verification code has been resent to your email');
      setCountdown(60); // 60 second cooldown
    } catch (error) {
      console.error('Resend error:', error);
      Error(
        error?.response?.data?.message ||
          'Failed to resend code. Please try again.'
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const isFormValid = !validateEmail(email) && !validateCode(code);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-green-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex">
        {/* Left Side - Info Panel */}
        <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-12 text-white">
          <div className="max-w-md">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mr-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Partner Program
              </h1>
            </div>

            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Just One Step Away from
              <span className="block bg-gradient-to-r from-green-400 to-green-500 bg-clip-text text-transparent">
                Your Success
              </span>
            </h2>

            <p className="text-lg text-gray-300 mb-8">
              Verify your email address to complete your registration and start
              earning commissions immediately.
            </p>

            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Account security verification
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Shield className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Protected from unauthorized access
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center mr-3">
                  <Clock className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-300">
                  Quick verification process
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Verification Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md">
            {/* Mobile Header */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 rounded-2xl flex items-center justify-center">
                  <Mail className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Verify Your Email
              </h1>
              <p className="text-gray-400">
                Check your inbox for the verification code
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
              <div className="hidden lg:block text-center mb-8">
                <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-400/20 to-green-500/20 rounded-2xl flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Verify Your Email
                </h2>
                <p className="text-gray-400">
                  Enter the code we sent to your email
                </p>
              </div>

              <div className="space-y-6">
                <div className="space-y-4">
                  <FlexibleInput
                    placeholder="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => handleBlur('email')}
                    icon={Mail}
                    iconClass="text-green-400"
                    error={errors.email}
                    touched={touchedFields.email}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
                  />

                  <FlexibleInput
                    placeholder="Enter Code"
                    type="text"
                    value={code}
                    onChange={(e) =>
                      setCode(
                        e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 4)
                      )
                    }
                    onBlur={() => handleBlur('code')}
                    onKeyPress={handleKeyPress}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20 text-center text-xl tracking-[0.5em] font-mono"
                    error={errors.code}
                    touched={touchedFields.code}
                  />
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-400 mb-4">
                    Didn't receive the code?
                  </p>
                  <Button
                    text={
                      countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'
                    }
                    onClick={handleResendCode}
                    loading={isResending}
                    disabled={countdown > 0}
                    variant="outline"
                    size="small"
                    icon={RefreshCw}
                    className="bg-gray-800/50 border-gray-600 text-white hover:bg-gray-700 hover:border-green-400 transition-all duration-300"
                  />
                </div>

                <Button
                  text="Verify Email"
                  onClick={handleVerify}
                  loading={loading}
                  disabled={!isFormValid}
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                  size="large"
                  icon={ArrowRight}
                  iconPosition="right"
                />

                <div className="text-center">
                  <p className="text-gray-400">
                    Need to change your email?{' '}
                    <Link
                      to="/auth/register"
                      className="text-green-400 hover:text-green-300 font-medium transition-colors"
                    >
                      Go back to registration
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-6">
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                Check your spam folder if you don't see the email in your inbox
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;
